import { type FormEvent, type JSX, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button, Card, Input, Skeleton } from '@/components/ui';
import { Icon } from '@/components/icons';
import { usePlans } from '@/hooks/useAdminPlans';
import {
  useDeletePlanOverride,
  usePlanOverrides,
  useUpsertPlanOverride,
  type PlanOverride,
  type PlanOverrideUpsertInput,
} from '@/hooks/useAdminPlanOverrides';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api';
import { can } from '@/lib/permissions';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';

// =============================================================================
// Page
// =============================================================================

/**
 * /admin/plans/:id/overrides — manages per-destination retail overrides
 * for a single plan. Overrides win at dispatch time over the rate-card
 * cascade for matching (prefix, message_type) shapes (PR #94 of the
 * billing alignment).
 *
 * Backend already enforces wholesale-floor validation on save —
 * sell_price below the platform's wholesale cost across the plan's
 * subscribers is rejected with 422 + a useful message.
 */
export default function AdminPlanOverridesPage(): JSX.Element {
  const { t } = useTranslation('default');
  const { id } = useParams<{ id: string }>();
  const planId = Number(id);
  const validPlanId = Number.isFinite(planId) && planId > 0;

  const { user } = useAuth();
  const canEdit = can.useAdminModule(user?.role);
  const plans = usePlans();
  const overrides = usePlanOverrides(planId, { enabled: validPlanId });
  const remove = useDeletePlanOverride(planId);

  const plan = useMemo(
    () => plans.data?.items.find((p) => p.id === planId) ?? null,
    [plans.data, planId],
  );

  const [editing, setEditing] = useState<PlanOverride | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<PlanOverride | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  if (!validPlanId) {
    return (
      <div className="px-6 py-6 max-w-[1400px] mx-auto">
        <Card padding="md">
          <div className="text-[13px] text-danger">{t('apo_invalid_id')}</div>
        </Card>
      </div>
    );
  }

  const handleSaved = () => {
    setShowForm(false);
    setEditing(null);
  };

  const handleDelete = async (o: PlanOverride): Promise<void> => {
    setDeleteError(null);
    try {
      await remove.mutateAsync(o.id);
      setConfirmDelete(null);
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : t('apo_err_delete'));
    }
  };

  return (
    <div className="px-6 py-6 max-w-[1400px] mx-auto">
      <div className="mb-5">
        <Link
          to="/admin/plans"
          className="inline-flex items-center gap-1.5 text-[13px] text-text-3 hover:text-text mb-2"
        >
          <Icon.chevronLeft width="14" height="14" />
          {t('apo_back_to_plans')}
        </Link>
        <h1 className="display text-[22px] font-semibold tracking-[-0.015em] text-text leading-[1.2] m-0">
          {plan ? t('apo_title_with_plan', { plan: plan.name }) : t('apo_title_loading')}
        </h1>
        <p className="text-[13px] text-text-3 mt-0.5 max-w-[720px]">
          {t('apo_subtitle')}
        </p>
      </div>

      <Card padding="none" className="mb-4">
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <div className="text-[13px] font-medium text-text">{t('apo_table_title')}</div>
          {canEdit && (
            <Button
              size="sm"
              onClick={() => {
                setEditing(null);
                setShowForm(true);
              }}
            >
              <Icon.plus width="14" height="14" />
              <span>{t('apo_add')}</span>
            </Button>
          )}
        </div>

        {overrides.isLoading ? (
          <div className="px-4 py-6">
            <Skeleton className="h-32 w-full" />
          </div>
        ) : overrides.error ? (
          <div className="px-4 py-6 text-[13px] text-danger">
            {t('apo_err_load', {
              error: overrides.error instanceof Error ? overrides.error.message : 'unknown',
            })}
          </div>
        ) : (overrides.data?.items ?? []).length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="text-[14px] text-text-2">{t('apo_empty_title')}</p>
            <p className="text-[12px] text-text-3 mt-1">{t('apo_empty_hint')}</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em] border-b border-line">
                <th className="py-2 px-3 text-left">{t('apo_col_prefix')}</th>
                <th className="py-2 px-3 text-left">{t('apo_col_message_type')}</th>
                <th className="py-2 px-3 text-right">{t('apo_col_sell_price')}</th>
                <th className="py-2 px-3 text-right">{t('apo_col_priority')}</th>
                <th className="py-2 px-3 text-left">{t('apo_col_window')}</th>
                <th className="py-2 px-3 text-center">{t('apo_col_active')}</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {(overrides.data?.items ?? []).map((o) => (
                <OverrideRow
                  key={o.id}
                  row={o}
                  onEdit={() => {
                    setEditing(o);
                    setShowForm(true);
                  }}
                  onDelete={() => setConfirmDelete(o)}
                  canEdit={canEdit}
                />
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {showForm && (
        <OverrideFormModal
          planId={planId}
          editing={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSaved={handleSaved}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          row={confirmDelete}
          error={deleteError}
          submitting={remove.isPending}
          onConfirm={() => void handleDelete(confirmDelete)}
          onCancel={() => {
            setConfirmDelete(null);
            setDeleteError(null);
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// Row
// =============================================================================

interface OverrideRowProps {
  row: PlanOverride;
  onEdit: () => void;
  onDelete: () => void;
  canEdit: boolean;
}

function OverrideRow({ row, onEdit, onDelete, canEdit }: OverrideRowProps): JSX.Element {
  const { t } = useTranslation('default');
  const window = formatWindow(row.effective_from, row.effective_to);
  return (
    <tr className="border-b border-line hover:bg-surface-2">
      <td className="py-2.5 px-3 text-[13px] text-text font-mono">
        {row.prefix === '*' ? <span className="text-text-3">{t('apo_prefix_catch_all')}</span> : row.prefix}
      </td>
      <td className="py-2.5 px-3 text-[13px] text-text-2">
        {row.message_type ? row.message_type : <span className="text-text-3">{t('apo_msg_type_any')}</span>}
      </td>
      <td className="py-2.5 px-3 text-[13px] text-text text-right tabular-nums">
        {formatCurrency(row.sell_price)}
      </td>
      <td className="py-2.5 px-3 text-[12px] text-text-3 text-right tabular-nums">{row.priority}</td>
      <td className="py-2.5 px-3 text-[12px] text-text-3">{window}</td>
      <td className="py-2.5 px-3 text-center">
        <span
          className={cn(
            'inline-block px-2 py-0.5 rounded-full text-[11px] font-medium',
            row.is_active
              ? 'bg-success-bg text-success border border-success-bd'
              : 'bg-surface-3 text-text-3 border border-line',
          )}
        >
          {row.is_active ? t('apo_status_active') : t('apo_status_paused')}
        </span>
      </td>
      <td className="py-2.5 px-3 text-right">
        {canEdit ? (
          <>
            <button
              type="button"
              onClick={onEdit}
              className="text-[12px] text-text-2 hover:text-text mr-3"
            >
              {t('apo_action_edit')}
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="text-[12px] text-danger hover:underline"
            >
              {t('apo_action_delete')}
            </button>
          </>
        ) : (
          <span className="text-text-4 text-[12px]">—</span>
        )}
      </td>
    </tr>
  );
}

function formatWindow(from: string, to?: string): string {
  const f = new Date(from).toLocaleDateString();
  if (!to) return `${f} →`;
  const t = new Date(to).toLocaleDateString();
  return `${f} → ${t}`;
}

// =============================================================================
// Form modal (create + edit)
// =============================================================================

interface OverrideFormModalProps {
  planId: number;
  editing: PlanOverride | null;
  onClose: () => void;
  onSaved: () => void;
}

function OverrideFormModal({
  planId: _planId,
  editing,
  onClose,
  onSaved,
}: OverrideFormModalProps): JSX.Element {
  const { t } = useTranslation('default');
  const upsert = useUpsertPlanOverride(_planId);
  const isEdit = editing !== null;
  const submitting = upsert.isPending;

  const [form, setForm] = useState({
    prefix: editing?.prefix ?? '',
    messageType: editing?.message_type ?? '',
    sellPrice: editing ? String(editing.sell_price) : '',
    priority: String(editing?.priority ?? 100),
    effectiveFrom: editing?.effective_from ? toLocalDateTime(editing.effective_from) : '',
    effectiveTo: editing?.effective_to ? toLocalDateTime(editing.effective_to) : '',
    isActive: editing?.is_active ?? true,
    notes: editing?.notes ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    const sellPrice = parseFloat(form.sellPrice);
    const priority = parseInt(form.priority, 10);

    if (form.prefix.trim() === '') {
      setError(t('apo_err_prefix_required'));
      return;
    }
    if (!Number.isFinite(sellPrice) || sellPrice < 0) {
      setError(t('apo_err_sell_price'));
      return;
    }
    if (!Number.isFinite(priority) || priority < 0) {
      setError(t('apo_err_priority'));
      return;
    }

    const input: PlanOverrideUpsertInput = {
      prefix: form.prefix.trim(),
      message_type: form.messageType.trim() || undefined,
      sell_price: sellPrice,
      priority,
      effective_from: form.effectiveFrom ? new Date(form.effectiveFrom).toISOString() : undefined,
      effective_to: form.effectiveTo ? new Date(form.effectiveTo).toISOString() : undefined,
      is_active: form.isActive,
      notes: form.notes.trim() || undefined,
    };

    try {
      await upsert.mutateAsync(input);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('apo_err_save'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[560px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">
            {isEdit ? t('apo_form_title_edit') : t('apo_form_title_create')}
          </h2>
          <p className="text-[12px] text-text-3 mt-0.5">{t('apo_form_subtitle')}</p>
        </div>
        <form onSubmit={(e) => void submit(e)} className="px-5 py-4 space-y-4">
          {error && (
            <div className="px-3 py-2 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('apo_field_prefix')} hint={t('apo_field_prefix_hint')}>
              <Input
                value={form.prefix}
                onChange={(e) => setForm((p) => ({ ...p, prefix: e.target.value }))}
                placeholder="1305"
                required
              />
            </Field>
            <Field label={t('apo_field_message_type')} hint={t('apo_field_message_type_hint')}>
              <select
                value={form.messageType}
                onChange={(e) => setForm((p) => ({ ...p, messageType: e.target.value }))}
                className="w-full h-9 px-3 text-[13px] bg-surface border border-line rounded-md focus:outline-none focus:border-volt-600"
              >
                <option value="">{t('apo_msg_type_any')}</option>
                <option value="marketing">marketing</option>
                <option value="transactional">transactional</option>
                <option value="2fa">2fa</option>
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('apo_field_sell_price')} hint={t('apo_field_sell_price_hint')}>
              <Input
                type="number"
                step="0.0001"
                min="0"
                value={form.sellPrice}
                onChange={(e) => setForm((p) => ({ ...p, sellPrice: e.target.value }))}
                placeholder="0.0042"
                required
              />
            </Field>
            <Field label={t('apo_field_priority')} hint={t('apo_field_priority_hint')}>
              <Input
                type="number"
                min="0"
                value={form.priority}
                onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('apo_field_effective_from')} hint={t('apo_field_effective_from_hint')}>
              <Input
                type="datetime-local"
                value={form.effectiveFrom}
                onChange={(e) => setForm((p) => ({ ...p, effectiveFrom: e.target.value }))}
              />
            </Field>
            <Field label={t('apo_field_effective_to')} hint={t('apo_field_effective_to_hint')}>
              <Input
                type="datetime-local"
                value={form.effectiveTo}
                onChange={(e) => setForm((p) => ({ ...p, effectiveTo: e.target.value }))}
              />
            </Field>
          </div>

          <Field label={t('apo_field_notes')}>
            <Input
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              placeholder={t('apo_field_notes_placeholder')}
            />
          </Field>

          <label className="flex items-center gap-2 text-[13px] text-text-2 select-none cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
            />
            {t('apo_field_active')}
          </label>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-line">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              {t('apo_action_cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('apo_action_saving') : t('apo_action_save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// `<input type="datetime-local">` wants `YYYY-MM-DDTHH:MM` (no
// seconds, no timezone). Convert RFC3339 from the API to that shape
// in the user's local timezone — round-trips back through new Date()
// in the submit handler so the wire format stays UTC.
function toLocalDateTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// =============================================================================
// Confirm delete
// =============================================================================

interface ConfirmDeleteProps {
  row: PlanOverride;
  error: string | null;
  submitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDeleteModal({
  row,
  error,
  submitting,
  onConfirm,
  onCancel,
}: ConfirmDeleteProps): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[440px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4">
          <h2 className="display text-[16px] font-semibold text-text">
            {t('apo_delete_title')}
          </h2>
          <p className="text-[13px] text-text-2 mt-2 leading-relaxed">
            {t('apo_delete_body', {
              prefix: row.prefix,
              price: formatCurrency(row.sell_price),
            })}
          </p>
          {error && (
            <div className="mt-3 px-3 py-2 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
              {error}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-line flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            {t('apo_action_cancel')}
          </Button>
          <Button type="button" variant="danger" onClick={onConfirm} disabled={submitting}>
            {submitting ? t('apo_action_deleting') : t('apo_action_confirm_delete')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Field
// =============================================================================

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="block text-[12px] font-medium text-text-2 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-text-3 mt-1">{hint}</p>}
    </div>
  );
}
